export interface RipButtonProps {
  onClick?: () => void;
  size?: 'md' | 'lg';
  /** Quantity label — renders as `${count} REPACKZ`. */
  count?: number;
  /** Display price (formatted by caller). */
  priceLabel?: string;
  disabled?: boolean;
}

/**
 * Per-breakpoint dimensions read directly from Figma:
 *
 * Mobile  (35:15118): outer 140, gradient 126 (inset 7),  green 114 (inset 13)
 * Desktop (17:5533) : outer 203, gradient 183 (inset 10), green 165 (inset 19)
 */
/**
 * Dark-pill clip-path (matches Figma `imgEllipse307` shape):
 *
 *   inset(0 round  TL  TR  BR  BL   /   TL  TR  BR  BL)
 *                 px  px  %   %        px  px   %   %
 *
 * Top corners — small, circular (22 / 26 px).
 * Bottom corners — elliptical: 50% horizontal (two corners meet at
 * mid-bottom ⇒ continuous arc), **22% vertical** (gentle shallow arc
 * matching Figma — a pronounced semi-ellipse would be too dramatic).
 */
/**
 * Pill shape — rounded top corners only, FLAT bottom. Pill extends
 * through the green-disc bottom boundary; the disc's own
 * `rounded-full overflow-hidden` (on the wrapper) clips pill's bottom
 * to the disc's CIRCULAR arc. This guarantees the bottom curve is
 * exactly concentric with the disc (same center, same radius) — unlike
 * any elliptical clip-path which has different math.
 */
const PILL_CLIP_MD = 'inset(0 round 22px 22px 0 0)';
const PILL_CLIP_LG = 'inset(0 round 26px 26px 0 0)';

/** Common inset-shadow giving the pill a subtly pressed-in feel. */
const PILL_INNER_SHADOW =
  'inset 0 1px 2px 0 rgba(0,0,0,0.20), inset 0 -1px 0 0 rgba(255,255,255,0.06)';

const SIZES = {
  md: {
    outer: 'h-[140px] w-[140px]',
    gradientInset: 'inset-[7px]',
    greenInset: 'inset-[13px]',
    rip: 'text-[32px] leading-[39px] tracking-[-0.54px] font-extrabold',
    ripY: 'top-[20%]',
    // Pill extends 10 % below disc bottom → disc-clip trims the overflow
    // to the circular arc. Narrow horizontal inset keeps the visible
    // straight sides short so the side→arc kink is minimal.
    pillBox: 'left-[5%] right-[5%] top-[54%] bottom-[-10%]',
    // Text box — constrained to the *visible* pill area so text never
    // falls into the clipped-off zone.
    textBox: 'left-[5%] right-[5%] top-[56%] bottom-[5%]',
    pillClip: PILL_CLIP_MD,
    repackz: 'text-[11px] leading-[20px] tracking-[-0.19px] font-bold',
    price: 'text-[17px] leading-[20px] tracking-[-0.19px] font-bold',
  },
  lg: {
    outer: 'h-[203px] w-[203px]',
    gradientInset: 'inset-[10px]',
    greenInset: 'inset-[19px]',
    rip: 'text-[46px] leading-[57px] tracking-[-0.78px] font-extrabold',
    ripY: 'top-[20%]',
    pillBox: 'left-[5%] right-[5%] top-[54%] bottom-[-10%]',
    textBox: 'left-[5%] right-[5%] top-[56%] bottom-[5%]',
    pillClip: PILL_CLIP_LG,
    repackz: 'text-[16px] leading-[29px] tracking-[-0.27px] font-bold',
    price: 'text-[24px] leading-[29px] tracking-[-0.27px] font-bold',
  },
} as const;

/**
 * Circular buy button with RIP! label — Figma 17:5533 (desktop) and
 * 35:15118 (mobile).
 *
 * Layering, outside → in:
 *   1. Outer white "collar" disc — the plate the rail curves around.
 *   2. Gradient mid-ring #E0E0E0 → #FFFFFF with a thin `#F2F2F2` border.
 *   3. Inner green disc — `var(--ui-accent)` fill with a strong white
 *      bloom inset from the bottom so the top half looks glossy.
 *   4. Dark inner pill — rounded rectangle sitting in the lower-middle
 *      of the disc with a vertical gradient (top lighter, bottom darker)
 *      and a soft inner shadow so the stack feels pressed into the disc.
 *      Both the `{count} REPACKZ` and `{priceLabel}` text lines live
 *      centered inside this pill.
 *   5. RIP! label — centred above the pill.
 */
export function RipButton({
  onClick,
  size = 'md',
  count = 1,
  priceLabel = '$0',
  disabled = false,
}: RipButtonProps) {
  const s = SIZES[size];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-co-component="RipButton"
      aria-label={`Open ${count} pack${count === 1 ? '' : 's'} for ${priceLabel}`}
      className={
        s.outer +
        ' relative shrink-0 rounded-full cursor-pointer ' +
        '[background:var(--co-RipButton-outerCollar,#fff)] ' +
        '[box-shadow:0_14px_64px_0_rgba(0,0,0,0.15),0_0_4px_0_color-mix(in_srgb,var(--co-RipButton-discFill,var(--ui-accent))_30%,transparent)] ' +
        'transition-transform active:scale-[0.98] hover:brightness-[1.03] ' +
        'disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100'
      }
    >
      {/* Gradient mid-ring */}
      <span
        aria-hidden
        className={
          `absolute ${s.gradientInset} rounded-full border border-[#F2F2F2] ` +
          '[background-image:linear-gradient(180deg,#E0E0E0_0%,#FFFFFF_106%)]'
        }
      />

      {/* Inner green disc — no extra border, softer top bloom */}
      <span
        aria-hidden
        className={
          `absolute ${s.greenInset} rounded-full overflow-hidden ` +
          '[background:var(--co-RipButton-discFill,var(--ui-accent))] ' +
          '[box-shadow:inset_0_-6px_44px_0_rgba(255,255,255,0.55)]'
        }
      />

      {/* RIP! label — upper half of the disc (ExtraBold 800) */}
      <span
        className={
          `${s.ripY} absolute left-0 right-0 text-center [font-family:var(--font-primary,_var(--font-sans))] [color:var(--co-RipButton-discText,#fff)] ${s.rip}`
        }
      >
        RIP!
      </span>

      {/* Dark pill — wrapper matches green disc geometry (same
          `greenInset` + `rounded-full overflow-hidden`) so %-positions
          inside are relative to the green disc (not the outer button)
          and any overflow past disc edges is clipped. Pill shape itself
          is a `clip-path: inset(... round ...)` — rounded top,
          elliptical bottom arc. */}
      <span
        aria-hidden
        className={`absolute ${s.greenInset} rounded-full overflow-hidden pointer-events-none`}
      >
        <span
          className={`absolute ${s.pillBox}`}
          style={{
            clipPath: s.pillClip,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.38) 100%)',
            boxShadow: PILL_INNER_SHADOW,
          }}
        />
      </span>

      {/* Quantity + price — positioned inside the disc-clip wrapper at
          `textBox` (narrower than `pillBox`) so text never lands in the
          part of the pill rectangle that extends past the disc. Font
          weight Bold (700) per Figma — not extrabold. */}
      <span
        className={`absolute ${s.greenInset} rounded-full overflow-hidden pointer-events-none`}
      >
        <span
          className={`${s.textBox} absolute flex flex-col items-center justify-center [font-family:var(--font-primary,_var(--font-sans))] [color:var(--co-RipButton-pillText,#fff)]`}
        >
          <span className={s.repackz + ' uppercase opacity-70'}>
            {count} REPACKZ
          </span>
          <span className={s.price}>{priceLabel}</span>
        </span>
      </span>
    </button>
  );
}
